/**
 * Supported interviewer languages - intersection of AssemblyAI STT and ElevenLabs TTS
 * Only languages supported by BOTH services are allowed
 */
export const SUPPORTED_INTERVIEWER_LANGUAGES = [
  // English variants
  "en",
  "en-US",
  "en-GB",
  "en-AU",
  "en-CA",
  "en-IN",
  "en-NZ",
  // Spanish variants
  "es",
  "es-ES",
  "es-MX",
  "es-AR",
  "es-CO",
  "es-CL",
  "es-PE",
  "es-VE",
  "es-EC",
  "es-GT",
  "es-CU",
  "es-BO",
  "es-DO",
  "es-HN",
  "es-PY",
  "es-SV",
  "es-NI",
  "es-CR",
  "es-PA",
  "es-UY",
  "es-PR",
  // Portuguese variants
  "pt",
  "pt-BR",
  "pt-PT",
  // Italian variants
  "it",
  "it-IT",
  "it-CH",
  // Japanese
  "ja",
  "ja-JP",
] as const;

export type SupportedInterviewerLanguage =
  (typeof SUPPORTED_INTERVIEWER_LANGUAGES)[number];

/**
 * The locales where every layer of the voice stack works, end-of-turn
 * detection included.
 *
 * LiveKit's `MultilingualModel` carries thresholds for exactly these 14. For
 * anything else `AudioRecognition` logs "turn detector does not support
 * language" and falls back to a fixed endpointing delay, so a study offered
 * in one of these is the only one whose turn-taking is model-driven rather
 * than a silence timer.
 *
 * Kept in sync with the model's own `languages.json` by a test in the agent
 * fleet, so a `livekit-agents` bump that changes the set fails loudly instead
 * of silently demoting a language to tier two.
 */
export const TIER_ONE_LOCALES = [
  "en-US",
  "es-ES",
  "pt-BR",
  "fr-FR",
  "de-DE",
  "it-IT",
  "nl-NL",
  "ru-RU",
  "tr-TR",
  "id-ID",
  "hi-IN",
  "zh-CN",
  "ja-JP",
  "ko-KR",
] as const;

/**
 * The locales an interview can run in, but with silence-based turn-taking.
 *
 * Deepgram `nova-3` transcribes all of them, and Cartesia either speaks them
 * natively or has a near relative to speak them as (see `CARTESIA_NEAREST` in
 * the agent fleet). What none of them has is a turn detector, so the agent
 * decides the participant has finished by waiting rather than by reading the
 * sentence. `screening/runner.py` gives these a longer endpointing delay to
 * compensate: slower to answer, in exchange for not talking over someone who
 * paused mid-thought.
 *
 * Deliberately absent, and why:
 *   - Armenian, Latvian, Lithuanian, Urdu: Deepgram hears them, Cartesia has
 *     neither a code nor a relative close enough to be worth claiming.
 *   - Malayalam: Cartesia speaks it, Deepgram cannot transcribe it, and an
 *     interview we cannot hear is not an interview.
 */
export const TIER_TWO_LOCALES = [
  "ar-SA",
  "bg-BG",
  "bn-BD",
  "cs-CZ",
  "da-DK",
  "el-GR",
  "fi-FI",
  "gu-IN",
  "he-IL",
  "hr-HR",
  "hu-HU",
  "ka-GE",
  "kn-IN",
  "mr-IN",
  "ms-MY",
  "no-NO",
  "pa-IN",
  "pl-PL",
  "ro-RO",
  "sk-SK",
  "sv-SE",
  "ta-IN",
  "te-IN",
  "th-TH",
  "tl-PH",
  "uk-UA",
  "vi-VN",
  "af-ZA",
  "be-BY",
  "bs-BA",
  "ca-ES",
  "et-EE",
  "fa-IR",
  "mk-MK",
  "ne-NP",
  "sl-SI",
  "sr-RS",
] as const;

/**
 * Every locale a study may be offered in. Tier one first, so the picker leads
 * with the ones whose turn-taking is fully supported.
 */
export const BASE_INTERVIEW_LOCALES = [
  ...TIER_ONE_LOCALES,
  ...TIER_TWO_LOCALES,
] as const;

export type TierOneLocale = (typeof TIER_ONE_LOCALES)[number];
export type BaseInterviewLocale = (typeof BASE_INTERVIEW_LOCALES)[number];

const TIER_ONE_SET: ReadonlySet<string> = new Set(TIER_ONE_LOCALES);

/**
 * Whether this locale's interview gets model-driven turn-taking.
 *
 * The agent reads the same distinction to pick its endpointing delay, and the
 * builder reads it to group the picker. Both need the same answer, which is
 * why it lives here rather than being re-derived on either side.
 */
export function hasTurnDetection(locale: string): locale is TierOneLocale {
  return TIER_ONE_SET.has(locale);
}

/**
 * Display names, in the language itself. A Spanish speaker picking their
 * language should not have to find "Spanish" in an English list.
 */
export const INTERVIEW_LOCALE_LABELS: Record<BaseInterviewLocale, string> = {
  "en-US": "English",
  "es-ES": "Español",
  "pt-BR": "Português",
  "fr-FR": "Français",
  "de-DE": "Deutsch",
  "it-IT": "Italiano",
  "nl-NL": "Nederlands",
  "ru-RU": "Русский",
  "tr-TR": "Türkçe",
  "id-ID": "Bahasa Indonesia",
  "hi-IN": "हिन्दी",
  "zh-CN": "中文",
  "ja-JP": "日本語",
  "ko-KR": "한국어",
  "ar-SA": "العربية",
  "bg-BG": "Български",
  "bn-BD": "বাংলা",
  "cs-CZ": "Čeština",
  "da-DK": "Dansk",
  "el-GR": "Ελληνικά",
  "fi-FI": "Suomi",
  "gu-IN": "ગુજરાતી",
  "he-IL": "עברית",
  "hr-HR": "Hrvatski",
  "hu-HU": "Magyar",
  "ka-GE": "ქართული",
  "kn-IN": "ಕನ್ನಡ",
  "mr-IN": "मराठी",
  "ms-MY": "Bahasa Melayu",
  "no-NO": "Norsk",
  "pa-IN": "ਪੰਜਾਬੀ",
  "pl-PL": "Polski",
  "ro-RO": "Română",
  "sk-SK": "Slovenčina",
  "sv-SE": "Svenska",
  "ta-IN": "தமிழ்",
  "te-IN": "తెలుగు",
  "th-TH": "ไทย",
  "tl-PH": "Tagalog",
  "uk-UA": "Українська",
  "vi-VN": "Tiếng Việt",
  "af-ZA": "Afrikaans",
  "be-BY": "Беларуская",
  "bs-BA": "Bosanski",
  "ca-ES": "Català",
  "et-EE": "Eesti",
  "fa-IR": "فارسی",
  "mk-MK": "Македонски",
  "ne-NP": "नेपाली",
  "sl-SI": "Slovenščina",
  "sr-RS": "Српски",
};

/**
 * Deliberately tier one only, and NOT the full catalog.
 *
 * The one consumer is `resolveAllInterviewLanguagesFromFilter`, which serves
 * the legacy interview flow. Widening this map would route that flow into
 * languages it cannot actually run, silently, off a targeting filter nobody
 * set for that purpose.
 *
 * The screening agent does not read this. It takes the locale the expert
 * picked, so it needs no prefix guessing.
 */
const PREFIX_TO_LOCALE: Record<string, TierOneLocale> = {
  en: "en-US",
  es: "es-ES",
  pt: "pt-BR",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  nl: "nl-NL",
  ru: "ru-RU",
  tr: "tr-TR",
  id: "id-ID",
  hi: "hi-IN",
  zh: "zh-CN",
  ja: "ja-JP",
  ko: "ko-KR",
};

/**
 * Default interview persona voices (ElevenLabs voice IDs).
 * Used by initialize-interview and the moderator agent.
 */
export const DEFAULT_PERSONA = {
  name: "Angela",
  elevenlabsVoiceId: "FUfBrNit0NNZAwb58KWH",
  elevenlabsModel: "eleven_flash_v2_5",
} as const;

export const JAPANESE_PERSONA = {
  name: "Chii-chan",
  elevenlabsVoiceId: "GxhGYQesaQaYKePCZDEC",
  elevenlabsModel: "eleven_flash_v2_5",
} as const;

export type InterviewPersona = typeof DEFAULT_PERSONA | typeof JAPANESE_PERSONA;

/**
 * Returns the interview persona for a given locale.
 */
export function getPersonaForLanguage(locale: string): InterviewPersona {
  const base = locale.split("-")[0];
  if (base === "ja") {
    return JAPANESE_PERSONA;
  }
  return DEFAULT_PERSONA;
}

/**
 * Resolve a study's LANGUAGE hard-filter values to a supported interview locale.
 * Checks for direct match first, then prefix match. Defaults to "en-US".
 */
export function resolveInterviewLanguageFromFilter(
  filterValues: string[],
): BaseInterviewLocale {
  const resolved = resolveAllInterviewLanguagesFromFilter(filterValues);
  return resolved[0] ?? "en-US";
}

/**
 * Resolve ALL matching interview locales from a study's LANGUAGE hard-filter.
 * Returns unique locales in order, deduplicating prefix matches.
 */
export function resolveAllInterviewLanguagesFromFilter(
  filterValues: string[],
): BaseInterviewLocale[] {
  const supported = new Set<string>(BASE_INTERVIEW_LOCALES);
  const result = new Set<BaseInterviewLocale>();

  for (const val of filterValues) {
    if (supported.has(val)) {
      result.add(val as BaseInterviewLocale);
    }
  }

  for (const val of filterValues) {
    const prefix = val.split("-")[0];
    const mapped = prefix ? PREFIX_TO_LOCALE[prefix] : undefined;
    if (mapped && !result.has(mapped)) {
      result.add(mapped);
    }
  }

  return [...result];
}

/**
 * Self-reported fluency in a language the person lists on their profile.
 * Mirrors the scale LinkedIn and most résumés already use, so an imported
 * profile maps onto it without a judgement call.
 */
export const LANGUAGE_PROFICIENCY_LEVELS = [
  { slug: "elementary", label: "Elementary proficiency" },
  { slug: "limited-working", label: "Limited working proficiency" },
  { slug: "professional-working", label: "Professional working proficiency" },
  { slug: "full-professional", label: "Full professional proficiency" },
  { slug: "native-bilingual", label: "Native or bilingual proficiency" },
] as const;

export type LanguageProficiency =
  (typeof LANGUAGE_PROFICIENCY_LEVELS)[number]["slug"];

const PROFICIENCY_SLUGS = new Set<string>(
  LANGUAGE_PROFICIENCY_LEVELS.map((level) => level.slug),
);

export function isLanguageProficiency(
  value: unknown,
): value is LanguageProficiency {
  return typeof value === "string" && PROFICIENCY_SLUGS.has(value);
}

/**
 * Position on the scale above; -1 for an unstated or unrecognised level, which
 * ranks below every stated one. Callers collapsing two claims about the same
 * language keep the higher, because the lower is the one its owner contradicted.
 */
export function languageProficiencyRank(
  proficiency: string | null | undefined,
): number {
  return LANGUAGE_PROFICIENCY_LEVELS.findIndex(
    (level) => level.slug === proficiency,
  );
}

export function languageProficiencyLabel(value: string): string {
  return (
    LANGUAGE_PROFICIENCY_LEVELS.find((level) => level.slug === value)?.label ??
    value
  );
}

/**
 * Whether listing a language at this level should let its owner through a
 * study's LANGUAGE gate.
 *
 * Only "elementary" is excluded, and an unstated level counts. A study's
 * language filter asks the coarse question "can this person be interviewed in
 * X", and someone who put the language on their profile at all is answering
 * yes; an imported profile frequently states the language without a level, and
 * reading that silence as "no" would discard most of what enrichment finds.
 * Elementary is the one level whose own label says otherwise.
 */
export function proficiencySpeaksLanguage(
  proficiency: string | null | undefined,
): boolean {
  return proficiency !== "elementary";
}

/**
 * Map a free-text proficiency onto the scale above.
 *
 * Imported profiles word this every way there is: LinkedIn's own phrasing,
 * "Fluent", "C1", "mother tongue". Returns null when nothing matches, which the
 * profile stores as "language listed, level unstated" rather than guessing a
 * level the person never claimed.
 */
export function normalizeLanguageProficiency(
  raw: string | null | undefined,
): LanguageProficiency | null {
  if (!raw) {
    return null;
  }
  // Punctuation folded to spaces so our own slugs round-trip through here and
  // "full-professional" is read the same as "Full professional proficiency".
  const text = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  if (!text) {
    return null;
  }
  // Order is load-bearing: the longer phrases are substrings of each other's
  // shorter tests ("full professional" contains "professional").
  if (/\b(native|bilingual|mother tongue)\b/.test(text)) {
    return "native-bilingual";
  }
  if (/\b(full professional|fluent|c1|c2)\b/.test(text)) {
    return "full-professional";
  }
  if (/\b(professional|advanced|proficient|b2)\b/.test(text)) {
    return "professional-working";
  }
  if (/\b(limited|intermediate|conversational|working|b1)\b/.test(text)) {
    return "limited-working";
  }
  if (/\b(elementary|basic|beginner|a1|a2)\b/.test(text)) {
    return "elementary";
  }
  return null;
}

/** Every study runs in English unless someone says otherwise. */
/**
 * The same locales named in English.
 *
 * `INTERVIEW_LOCALE_LABELS` holds endonyms, which is right for the participant
 * picking the language of their own interview: a Korean speaker looks for
 * 한국어, not "Korean". It is the wrong label for the study builder, where a
 * researcher who reads neither Hangul nor Kannada cannot tell 한국어 from ಕನ್ನಡ
 * and has to guess which row is the language they meant.
 *
 * Both names, rather than replacing one with the other, because each is the
 * only useful one to somebody. `interviewLocaleLabelWithEnglish` renders the
 * pair for the researcher-facing surfaces.
 */
export const INTERVIEW_LOCALE_ENGLISH_LABELS: Record<
  BaseInterviewLocale,
  string
> = {
  "en-US": "English",
  "es-ES": "Spanish",
  "pt-BR": "Portuguese",
  "fr-FR": "French",
  "de-DE": "German",
  "it-IT": "Italian",
  "nl-NL": "Dutch",
  "ru-RU": "Russian",
  "tr-TR": "Turkish",
  "id-ID": "Indonesian",
  "hi-IN": "Hindi",
  "zh-CN": "Chinese",
  "ja-JP": "Japanese",
  "ko-KR": "Korean",
  "ar-SA": "Arabic",
  "bg-BG": "Bulgarian",
  "bn-BD": "Bangla",
  "cs-CZ": "Czech",
  "da-DK": "Danish",
  "el-GR": "Greek",
  "fi-FI": "Finnish",
  "gu-IN": "Gujarati",
  "he-IL": "Hebrew",
  "hr-HR": "Croatian",
  "hu-HU": "Hungarian",
  "ka-GE": "Georgian",
  "kn-IN": "Kannada",
  "mr-IN": "Marathi",
  "ms-MY": "Malay",
  "no-NO": "Norwegian",
  "pa-IN": "Punjabi",
  "pl-PL": "Polish",
  "ro-RO": "Romanian",
  "sk-SK": "Slovak",
  "sv-SE": "Swedish",
  "ta-IN": "Tamil",
  "te-IN": "Telugu",
  "th-TH": "Thai",
  "tl-PH": "Filipino",
  "uk-UA": "Ukrainian",
  "vi-VN": "Vietnamese",
  "af-ZA": "Afrikaans",
  "be-BY": "Belarusian",
  "bs-BA": "Bosnian",
  "ca-ES": "Catalan",
  "et-EE": "Estonian",
  "fa-IR": "Persian",
  "mk-MK": "Macedonian",
  "ne-NP": "Nepali",
  "sl-SI": "Slovenian",
  "sr-RS": "Serbian",
};

/**
 * "한국어 (Korean)", or plain "English" where the two names are the same.
 *
 * Also the string a picker should search: typing "korean" has to find the row
 * whose visible label is in Hangul, or the list is only usable by people who
 * already read it.
 */
export function interviewLocaleLabelWithEnglish(
  locale: BaseInterviewLocale,
): string {
  const endonym = INTERVIEW_LOCALE_LABELS[locale];
  const english = INTERVIEW_LOCALE_ENGLISH_LABELS[locale];
  return endonym === english ? endonym : `${endonym} (${english})`;
}

export const DEFAULT_STUDY_LANGUAGE: BaseInterviewLocale = "en-US";

export function isBaseInterviewLocale(
  value: string,
): value is BaseInterviewLocale {
  return (BASE_INTERVIEW_LOCALES as readonly string[]).includes(value);
}

/**
 * The locales a study is actually offered in, given whatever is on the row.
 *
 * Total rather than throwing, because every caller here is on a participant's
 * path into an interview: a study whose column predates this field, was
 * emptied by a bad patch, or names a locale a later deploy retired must still
 * open in English rather than fail. Order follows BASE_INTERVIEW_LOCALES so
 * two studies with the same set render their pickers identically.
 */
export function resolveStudyLanguages(
  stored: readonly string[] | null | undefined,
): BaseInterviewLocale[] {
  const offered = BASE_INTERVIEW_LOCALES.filter((locale) =>
    stored?.includes(locale),
  );
  return offered.length > 0 ? [...offered] : [DEFAULT_STUDY_LANGUAGE];
}

/**
 * The locale one participant's interview runs in.
 *
 * `chosen` is what they picked in the apply flow, and it is re-checked against
 * the study rather than trusted: the customer can narrow `supportedLanguages`
 * after an application is already in flight, and a choice the study no longer
 * offers must not reach the agent as a language nothing downstream is
 * configured for. Falls back to the study's first offered locale.
 */
export function resolveSubmissionLanguage(args: {
  studyLanguages: readonly string[] | null | undefined;
  chosen: string | null | undefined;
}): BaseInterviewLocale {
  const offered = resolveStudyLanguages(args.studyLanguages);
  const chosen = args.chosen;
  if (chosen && isBaseInterviewLocale(chosen) && offered.includes(chosen)) {
    return chosen;
  }
  return offered[0] ?? DEFAULT_STUDY_LANGUAGE;
}
